import React from "react";
import axios, { AxiosResponse } from "axios";
import moment from 'moment';
import { AlertColor, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormLabel, MenuItem, Paper, Radio, RadioGroup, TextField, useMediaQuery} from "@mui/material";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { getUserPreferences, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { RegionCombo } from "../regions/RegionAccessories";
import { BranchInfo } from "../branches/BranchAccessories";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { PurchaseOrderSalesSummaryViewer, PurchaseOrderSalesSummaryViewerHandle } from "./PurchaseOrderSalesSummaryViewer";
import NumberFormat from "react-number-format";
import { ValidateNumber } from "../../../common/CommonFunctions";
import { NumberTypes } from "../../../models/enums/NumberType";

export function PurchaseOrderSalesSummary(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);
	const [regionComboData, setRegionComboData] = React.useState<DropDownComboOption[]>([]);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

    const viewerModalRef = React.useRef<PurchaseOrderSalesSummaryViewerHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

	const [selectedRegion, setSelectedRegion] = React.useState<string>("");
	const [selectedStaffId, setSelectedStaffId] = React.useState<number>(0);
    const [dateType, setDateType] = React.useState<number>(0);
	const [fromDate, setFromDate] = React.useState<Date>(new Date());
    const [toDate, setToDate] = React.useState<Date>(new Date());
    const [shipmentType, setShipmentType] = React.useState<number>(0);
    const [reportType, setReportType] = React.useState<number>(0);
	const [showSalesman, setShowSalesman] = React.useState<boolean>(false);
	const [isExport, setIsExport] = React.useState<boolean>(false);
    const [exRateUsd, setExRateUsd] = React.useState<number>(90);
    const [exRateHkd, setExRateHkd] = React.useState<number>(11.5);
    const [exRateRmb, setExRateRmb] = React.useState<number>(10);
    const [usdExRateErrorText, setUsdExRateErrorText] = React.useState<string>("");
    const [hkdExRateErrorText, setHkdExRateErrorText] = React.useState<string>("");
    const [rmbExRateErrorText, setRmbExRateErrorText] = React.useState<string>("");

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();
		let lastMonth = new Date();
		lastMonth.setMonth((new Date()).getMonth() - 1);
		setFromDate(lastMonth);
		
		axios.all([
			axios.get<any[]>(`Api/V1/Region/Combo`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const regionComboResult: AxiosResponse<RegionCombo[]> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let loadedRegions: DropDownComboOption[] = [];
			branchInfoResult.data.map(b => {
				if (regionComboResult.data.find(r => r.regionName === b.region))
					loadedRegions.push({ name: b.companyName1, value: b.region });
			});
			setRegionComboData(loadedRegions);

			let preferences = getUserPreferences();
			if (preferences)
			{
				setSelectedRegion(preferences.defaultRegion);
			}
			setLoading(false);
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			console.log(error);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		})

    }

    function Preview() {
		viewerModalRef.current?.Initialise(selectedRegion, dateType, fromDate, toDate, selectedStaffId);
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Purchase Order Summary Report" />
				<Paper variant="outlined" className={style.searchBar} >
					<div style={{ display: 'flex'}}>
						<div className={style.ModalFields} style={{width: '100%'}}>
							<TextField style={{ left: 10, width: 80, marginTop: 10, paddingTop: "5px", paddingBottom: "5px" }}
								onChange={(e) => {setSelectedRegion(e.target.value)}}
								value = {selectedRegion}
								select
								label="Region"
								size="small"
							>
								{regionComboData.map((item)=> (
									<MenuItem key={item.value} value={item.value}>
										{item.value}
									</MenuItem>
								))}
							</TextField>
							<div style={{ width: 30 }}></div>
							<Autocomplete style={{ width: 200, marginTop: 10, paddingTop: "5px", paddingBottom: "5px" }}
								limitTags={2}
								onChange={(e, selected) => {setSelectedStaffId(parseInt(selected?.value ?? "0"))}}
								options={props.sales}
								getOptionLabel={(option: DropDownComboOption) => option.name}
								renderOption={(props, option) => {
								return (
									<li {...props} key={option.value}>
									{option.name}
									</li>
								);
								}}
								value={props.sales.find(s => s.value === selectedStaffId.toString()) || null}
								renderInput={(params) => (
								<TextField
									{...params}
									label={"Sales"}
								/>
								)}
								size="small"
							/>
							<div style={{ width: 10 }}></div>
							<div style={{ alignItems: 'start', marginLeft: 20, marginTop: 15 }}>
								<RadioGroup
									key="dateType"
									row
									value={dateType}
									defaultValue={dateType}
									name="date-type"
									onChange={(e) => setDateType(parseInt(e.target.value))}
								>
									<FormControlLabel value="0" control={<Radio />} label="Create Date" />
									<FormControlLabel value="2" control={<Radio />} label="Request Date" />
									<FormControlLabel value="1" control={<Radio />} label="Actual Date" />
								</RadioGroup>
							</div>
							<div style={{ width: 10 }}></div>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									key="fromDate"
									label="From"
									inputFormat="yyyy/MM/dd"
									mask="____/__/__"
									value={fromDate}
									onChange={(newValue) => setFromDate(newValue ?? new Date())}
									renderInput={(params) => 
										<TextField {...params} size="small"
											style={{ width: "160px", marginLeft: 10, marginTop: 15 }}
										/>
									}
								/>
							</LocalizationProvider>
							<div style={{ width: 10 }}></div>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									key="toDate"
									label="To"
									inputFormat="yyyy/MM/dd"
									mask="____/__/__"
									value={toDate}
									onChange={(newValue) => setToDate(newValue ?? new Date())}
									renderInput={(params) => 
										<TextField {...params} size="small"
											style={{ width: "160px", marginLeft: 10, marginTop: 15 }}
										/>
									}
								/>
							</LocalizationProvider>
							<div style={{ flexGrow: 1 }}></div>
							<div style={{ width: '100px', paddingTop: "12px", paddingLeft: "8px" }}>
								<Button type="submit" aria-controls="filter-menu" aria-haspopup="true" variant="contained" color="secondary" fullWidth onClick={(e)=> Preview()}>
									Preview
								</Button>
							</div>
							<div style={{ width: 10 }}></div>					

						</div>
					</div>
				</Paper>
				<div className={style.PageContent}>
			</div>

			<PurchaseOrderSalesSummaryViewer ref={viewerModalRef} onClose={()=>{}}/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

